/**
 * generateBlitzPayPDF.js
 *
 * Programmatic jsPDF renderer for BlitzPay invoices.
 *
 * WHY NOT html2canvas:
 *  - html2canvas captures the whole browser viewport including navigation,
 *    action bars, gradients, etc.
 *  - It requires the DOM to be rendered (auth required, page must be mounted).
 *  - This approach: pure vector PDF, auth-free, pixel-perfect, fast.
 *
 * Usage:
 *   import { generateBlitzPayPDF } from '../Utils/generateBlitzPayPDF';
 *   generateBlitzPayPDF(invoice, blitzpayProfile, payouts);
 */

import { jsPDF } from 'jspdf';

/* ─── Page geometry (mm) ──────────────────────────────────────────── */
const PW = 210;           // A4 width
const PH = 297;           // A4 height
const ML = 14;            // margin left
const MR = 14;            // margin right
const CW = PW - ML - MR; // 182 mm usable

/* ─── Brand colours [R, G, B] ────────────────────────────────────── */
const C = {
  accent:   [102, 126, 234],  // #667eea  (Blitz purple-blue)
  red:      [239,  68,  68],  // #ef4444
  redBg:    [254, 226, 226],  // #fee2e2
  redBord:  [254, 202, 202],  // #fecaca
  dark:     [ 30,  41,  59],  // #1e293b
  gray:     [100, 116, 139],  // #64748b
  lightBg:  [248, 250, 252],  // #f8fafc
  stripeBg: [245, 247, 250],
  border:   [226, 232, 240],  // #e2e8f0
  white:    [255, 255, 255],
};

/* ─── BlitzPay remittance fallback defaults ──────────────────────── */
const DEFAULTS = {
  company_name:  'Pangea Omninational Corporation',
  address_line1: '8724 Cavell Lane',
  city:          'Houston',
  state:         'TX',
  zip_code:      '77055',
  email:         'win@useblitz.co',
  terms:         'Make all checks payable to "DBA Pangea Omninational Corporation"',
  bank_details: {
    account_holder: 'Houston Alexander Roa',
    account_name:   'Pangea',
    bank_name:      'Prosperity Bank',
    bank_address:   '2117 N. Fry Road, Katy, TX 77449',
    account_number: '217841714',
    routing_number: '113122655',
    swift_code:     'PROYUS44',
  },
};

/* ─── Micro utilities ────────────────────────────────────────────── */
const safeNum = (v) => (typeof v === 'number' ? v : parseFloat(v) || 0);
const money   = (v) => `$${safeNum(v).toFixed(2)}`;
const safe    = (v) => (v != null ? String(v) : '');

function fmtDate(d) {
  if (!d) return 'N/A';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return String(d); }
}

function resolveDueDate(invoice) {
  if (!invoice) return null;
  if (invoice.payment_date) return invoice.payment_date;

  const createdAt = invoice.created_at;
  const paymentTerm = Number(invoice.payment_term);
  if (createdAt && Number.isFinite(paymentTerm) && paymentTerm > 0) {
    const originDate = new Date(createdAt);
    if (!Number.isNaN(originDate.getTime())) {
      const dueDate = new Date(originDate);
      dueDate.setDate(originDate.getDate() + paymentTerm);
      return dueDate;
    }
  }

  return createdAt || null;
}

function firstFiniteNumber(...candidates) {
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') continue;
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

const hasRenderableBankDetails = (details = {}) => Boolean(
  details.bank_name ||
  details.account_number ||
  details.routing_number
);

const LEGACY_GENERIC_TERMS = 'Make all checks payable to the company listed above.';

const getProfileTerms = (terms) => (
  terms && terms !== LEGACY_GENERIC_TERMS ? terms : ''
);

const getProfileSenderDisplayName = (profile = {}) => (
  profile?.sender_display_name ||
  profile?.invoice_sender_name ||
  profile?.company_name ||
  ''
);

const getProfileLegalCompanyName = (profile = {}) => (
  profile?.company_name ||
  profile?.invoice_sender_name ||
  profile?.sender_display_name ||
  ''
);

const getPayableTerms = (companyName) => (
  companyName
    ? `Make all checks payable to "${companyName}"`
    : DEFAULTS.terms
);

/* ════════════════════════════════════════════════════════════════════
   MAIN EXPORT
   ════════════════════════════════════════════════════════════════════ */
export function generateBlitzPayPDF(invoice, blitzpayProfile, payouts = [], fallbackBilledTo = {}) {

  /* 1 ─ Resolve billing data ─────────────────────────────────────── */
  const cd  = invoice?.custom_details  || {};
  const bfi = cd.billed_from_info      || {};
  const bti = cd.billed_to_info        || {};
  const ai  = {
    ...(cd.additional_info || {}),
    ...(bti.additional_info || {}),
  };
  const invoiceBankDetails = {
    ...(cd.additional_info?.bank_details || {}),
    ...(bti.bank_details || {}),
  };
  const bfiTrusted = bfi._confirmed === true ? bfi : {};
  const profileSenderDisplayName = getProfileSenderDisplayName(blitzpayProfile);
  const profileLegalCompanyName = getProfileLegalCompanyName(blitzpayProfile);

  // Sender / top-right — same priority as BlitzPayInvoiceDesign component
  const senderDisplay =
    bfiTrusted.sender_display_name       ||
    bfiTrusted.company_name              ||
    profileSenderDisplayName             ||
    DEFAULTS.company_name;

  const senderLegal =
    bfiTrusted.company_name       ||
    profileLegalCompanyName       ||
    DEFAULTS.company_name;

  const BF = {
    display:      senderDisplay,
    legal:        senderLegal,
    hasOverride:  senderDisplay !== senderLegal,
    addr1:  bfiTrusted.address_line1 || DEFAULTS.address_line1,
    addr2:  bfiTrusted.address_line2 || '',
    city:   bfiTrusted.city          || DEFAULTS.city,
    state:  bfiTrusted.state         || DEFAULTS.state,
    zip:    bfiTrusted.zip_code      || DEFAULTS.zip_code,
    email:  bfiTrusted.email         || DEFAULTS.email,
    phone:  bfiTrusted.phone         || '',
    taxId:  bfiTrusted.tax_id        || '',
  };

  const fallbackBilledToCompany = safe(fallbackBilledTo?.company_name || fallbackBilledTo?.companyName || '');
  const fallbackBilledToContact = safe(fallbackBilledTo?.contact_name || fallbackBilledTo?.contactName || '');
  const fallbackBilledToEmail = safe(fallbackBilledTo?.email || '');

  // Billed To — who owes us
  const BT = {
    company: bti.company_name  || invoice?.client_name  || fallbackBilledToCompany || '',
    contact: bti.contact_name  || fallbackBilledToContact || '',
    addr1:   bti.address_line1 || '',
    addr2:   bti.address_line2 || '',
    city:    bti.city          || '',
    state:   bti.state         || '',
    zip:     bti.zip_code      || '',
    country: bti.country       || '',
    email:   bti.email         || invoice?.client_email || fallbackBilledToEmail || '',
    phone:   bti.phone         || '',
    taxId:   bti.tax_id        || '',
  };

  // Bank details
  const profileBankDetails = blitzpayProfile?.bank_details || {};
  const hasInvoiceBankDetails = hasRenderableBankDetails(invoiceBankDetails);
  const hasFullProfileBankDetails = Boolean(
    profileBankDetails.account_holder ||
    profileBankDetails.account_name ||
    profileBankDetails.account_number ||
    profileBankDetails.routing_number ||
    profileBankDetails.swift_code ||
    profileBankDetails.iban
  );
  const bk = hasInvoiceBankDetails
    ? invoiceBankDetails
    : (hasFullProfileBankDetails ? profileBankDetails : DEFAULTS.bank_details);

  const BANK = {
    holder:   bk.account_holder || '',
    name:     bk.account_name   || '',
    bank:     bk.bank_name      || '',
    bankAddr: bk.bank_address   || '',
    acct:     bk.account_number || '',
    routing:  bk.routing_number || '',
    swift:    bk.swift_code     || bk.swift || '',
  };

  // Terms / notes
  const TERMS = {
    terms:        getProfileTerms(ai.terms_and_conditions) || getProfileTerms(blitzpayProfile?.terms?.default_terms) || getPayableTerms(senderLegal || senderDisplay),
    notes:        blitzpayProfile?.terms?.invoice_notes        || ai.notes                || '',
    instructions: blitzpayProfile?.terms?.payment_instructions || ai.payment_instructions || '',
  };

  // Vendor
  const VENDOR = {
    id:  blitzpayProfile?.vendor_info?.vendor_id  || '',
    erp: blitzpayProfile?.vendor_info?.erp_system || '',
  };

  const amountDue  = safeNum(invoice?.amount_due);
  const amountPaid = Math.min(
    Math.max(firstFiniteNumber(cd?.amount_paid) ?? 0, 0),
    amountDue
  );
  const outstanding = Math.max(amountDue - amountPaid, 0);
  const poNumber   = safe(invoice?.po_number);
  const blitzInvoiceId = safe(invoice?.id);
  const externalPO = safe(invoice?.external_po_number);
  const trimmedExternalPO = externalPO.trim();
  const invoiceNoteValue = safe(invoice?.notes).trim();
  const dueDateValue = resolveDueDate(invoice);
  const safePayouts = Array.isArray(payouts) ? payouts.filter(Boolean) : [];
  const explicitBlitzPayFee = firstFiniteNumber(
    cd?.blitzpay_fee_amount,
    cd?.fee_breakdown?.blitzpay_fee,
    cd?.blitzpay_fee,
  );
  const blitzPayFeePercent = firstFiniteNumber(
    cd?.blitzpay_fee_percentage,
    cd?.fee_breakdown?.blitzpay_fee_percentage,
    invoice?.fee_percentage,
  );

  /* 2 ─ Create document ──────────────────────────────────────────── */
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = 0;

  /* ── Low-level helpers ────────────────────────────────────────── */

  const sf = (bold, size, col) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    if (size != null) doc.setFontSize(size);
    if (col)          doc.setTextColor(...col);
  };

  const tx = (str, x, yy, opts) => {
    const s = String(str ?? '');
    if (!s) return;
    doc.text(s, x, yy, opts || undefined);
  };

  const hline = (yy, col, lw) => {
    doc.setDrawColor(...(col || C.border));
    doc.setLineWidth(lw || 0.25);
    doc.line(ML, yy, PW - MR, yy);
  };

  const fillRect = (x, yy, w, h, fill, stroke, sw) => {
    if (fill)   doc.setFillColor(...fill);
    if (stroke) { doc.setDrawColor(...stroke); doc.setLineWidth(sw || 0.25); }
    doc.rect(x, yy, w, h, fill && stroke ? 'FD' : fill ? 'F' : 'D');
  };

  const ensureSpace = (needed) => {
    if (y + needed > PH - 14) {
      doc.addPage();
      y = 14;
    }
  };

  /* 3 ─ Top accent gradient bar ──────────────────────────────────── */
  // jsPDF doesn't support real gradients, so approximate with two rects
  fillRect(0, 0, PW / 2, 5, [102, 126, 234]);
  fillRect(PW / 2, 0, PW / 2, 5, [118, 75, 162]);
  y = 14;

  /* 4 ─ HEADER ROW ───────────────────────────────────────────────── */
  const headerTop = y;

  // Left: "INVOICE" wordmark
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(...C.accent);
  doc.text('INVOICE', ML, y + 10);

  // Left sub: logo placeholder or company initial
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.gray);
  doc.text('BLITZPAY', ML, y + 16);

  // Right: sender identity
  const RX = PW - MR;
  let ry = headerTop;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.dark);
  doc.text(BF.display, RX, ry, { align: 'right' }); ry += 4.5;

  if (BF.hasOverride) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.gray);
    doc.text(`(${BF.legal})`, RX, ry, { align: 'right' }); ry += 4;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...C.gray);

  const addrFull = BF.addr1 + (BF.addr2 ? `, ${BF.addr2}` : '');
  doc.text(addrFull, RX, ry, { align: 'right' }); ry += 4;
  doc.text(`${BF.city}, ${BF.state} ${BF.zip}`, RX, ry, { align: 'right' }); ry += 4;
  if (BF.email) { doc.text(BF.email, RX, ry, { align: 'right' }); ry += 4; }
  if (BF.phone) { doc.text(BF.phone, RX, ry, { align: 'right' }); ry += 4; }
  if (BF.taxId) { doc.text(`Tax ID: ${BF.taxId}`, RX, ry, { align: 'right' }); ry += 4; }

  y = Math.max(headerTop + 20, ry) + 6;

  // Accent divider line matching the component's red <Divider>
  doc.setDrawColor(...C.red);
  doc.setLineWidth(0.6);
  doc.line(ML, y, PW - MR, y);
  y += 7;

  /* 5 ─ INFO ROW — three columns ─────────────────────────────────── */
  const infoY = y;
  const colW  = CW / 3;
  const C1X   = ML;
  const C2X   = ML + colW + 2;
  const C3X   = ML + colW * 2 + 4;
  const C3RX  = PW - MR;

  /* Column 1 — Billed To */
  sf(true, 6.5, C.red);
  tx('BILLED TO', C1X, infoY);
  let c1y = infoY + 4.5;

  sf(true, 9, C.dark);
  tx(BT.company || BT.contact || 'N/A', C1X, c1y); c1y += 4.5;

  sf(false, 7.5, C.gray);
  if (BT.company && BT.contact) { tx(`Attn: ${BT.contact}`, C1X, c1y); c1y += 3.8; }
  if (BT.addr1) {
    tx(BT.addr1 + (BT.addr2 ? `, ${BT.addr2}` : ''), C1X, c1y); c1y += 3.8;
    const cityLine = [BT.city, BT.state, BT.zip].filter(Boolean).join(', ');
    if (cityLine) { tx(cityLine, C1X, c1y); c1y += 3.8; }
    if (BT.country && BT.country !== 'USA') { tx(BT.country, C1X, c1y); c1y += 3.8; }
  }
  if (BT.email) { tx(BT.email, C1X, c1y); c1y += 3.8; }
  if (BT.phone) { tx(BT.phone, C1X, c1y); c1y += 3.8; }
  if (BT.taxId) { tx(`Tax ID: ${BT.taxId}`, C1X, c1y); c1y += 3.8; }

  /* Column 2 — Dates */
  let c2y = infoY;
  sf(true, 6.5, C.red);
  tx('DATE OF ISSUE', C2X, c2y); c2y += 4.5;
  sf(false, 8.5, C.dark);
  tx(fmtDate(invoice?.created_at), C2X, c2y); c2y += 9;

  sf(true, 6.5, C.red);
  tx('DUE DATE', C2X, c2y); c2y += 4.5;
  sf(false, 8.5, C.dark);
  tx(fmtDate(dueDateValue), C2X, c2y); c2y += 4;

  /* Column 3 — PO / Amount Due */
  let c3y = infoY;

  sf(true, 6.5, C.red);
  tx('BLITZ TRANSACTION PO', C3X, c3y); c3y += 4.5;
  doc.setFont('courier', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.dark);
  tx(poNumber || 'N/A', C3X, c3y); c3y += 8;

  if (blitzInvoiceId) {
    sf(true, 6.5, C.red);
    tx('BLITZ INVOICE ID', C3X, c3y); c3y += 4.5;
    doc.setFont('courier', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.dark);
    tx(blitzInvoiceId, C3X, c3y); c3y += 8;
  }

  if (VENDOR.id) {
    sf(true, 6.5, C.red);
    tx('VENDOR ID', C3X, c3y); c3y += 4.5;
    doc.setFont('courier', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.dark);
    tx(VENDOR.id + (VENDOR.erp ? ` (${VENDOR.erp})` : ''), C3X, c3y); c3y += 8;
  }

  const referenceValue = trimmedExternalPO || invoiceNoteValue;
  if (referenceValue) {
    sf(true, 6.5, C.red);
    tx('REFERENCE', C3X, c3y); c3y += 4.5;
    sf(false, 8, C.dark);
    tx(referenceValue, C3X, c3y); c3y += 8;
  }

  // Amount Due box (mirrors the red box in the component)
  c3y += 1;
  const boxW = colW - 4;
  const boxH = 18;
  fillRect(C3X, c3y, boxW, boxH, C.redBg, C.redBord, 0.3);
  sf(true, 6.5, C.red);
  tx('AMOUNT DUE (USD)', C3X + 2.5, c3y + 5.5);
  sf(true, 15, C.dark);
  tx(money(outstanding), C3X + 2.5, c3y + 14);
  c3y += boxH + 2;

  y = Math.max(c1y, c2y + 6, c3y) + 5;

  /* 6 ─ Red divider (matching component <Divider borderColor="#ef4444">) */
  ensureSpace(8);
  doc.setDrawColor(...C.red);
  doc.setLineWidth(0.6);
  doc.line(ML, y, PW - MR, y);
  y += 6;

  /* 7 ─ LINE ITEMS TABLE ─────────────────────────────────────────── */
  ensureSpace(22);

  const T = {
    desc:  { x: ML,              w: CW * 0.50 },
    rate:  { x: ML + CW * 0.50, w: CW * 0.18 },
    qty:   { x: ML + CW * 0.68, w: CW * 0.12 },
    total: { x: ML + CW * 0.80, w: CW * 0.20 },
  };
  const HDR_H = 7;
  const ROW_H = 7;

  // Header row — #f8fafc background
  fillRect(ML, y, CW, HDR_H, C.lightBg, C.border, 0.2);
  sf(true, 7.5, C.dark);
  doc.text('Description',
    T.desc.x + 2.5, y + 4.8);
  doc.text('Rate',
    T.rate.x + T.rate.w, y + 4.8, { align: 'right' });
  doc.text('Qty',
    T.qty.x + T.qty.w / 2, y + 4.8, { align: 'center' });
  doc.text('Line Total',
    T.total.x + T.total.w - 1, y + 4.8, { align: 'right' });
  y += HDR_H;

  // Build rows — mirror the component priority: invoice_items > payouts > fallback
  let rows = [];
  if (invoice?.invoice_items?.length > 0) {
    rows = invoice.invoice_items.map(item => ({
      desc:  item?.description || 'Service',
      rate:  safeNum(item?.rate),
      qty:   item?.quantity || 1,
      total: safeNum(item?.amount),
    }));
  } else if (safePayouts.length > 0) {
    rows = safePayouts.map((p, i) => {
      const name = p?.name || p?.creator_info?.display_name || p?.creator_id || `Vendor ${i + 1}`;
      const amt  = safeNum(p?.amount || p?.net_amount);
      return {
        desc:    p?.campaign_name ? `${name} — ${p.campaign_name}` : name,
        subDesc: p?.creator_id && p?.name ? `@${p.creator_id}` : '',
        rate:    amt,
        qty:     1,
        total:   amt,
      };
    });
  } else {
    rows = [{ desc: 'BlitzPay Services', rate: amountDue, qty: 1, total: amountDue }];
  }
  const canonicalSubtotal = firstFiniteNumber(cd?.subtotal_total, cd?.fee_breakdown?.subtotal_total);
  const displayedSubtotal = canonicalSubtotal !== null
    ? canonicalSubtotal
    : rows.reduce((sum, row) => sum + safeNum(row.total), 0);
  const fallbackBlitzPayFee = Math.max(amountDue - displayedSubtotal, 0);
  const normalizedExplicitBlitzPayFee = explicitBlitzPayFee !== null
    ? Math.max(explicitBlitzPayFee, 0)
    : null;
  const blitzPayFee = normalizedExplicitBlitzPayFee !== null && normalizedExplicitBlitzPayFee > 0.009
    ? normalizedExplicitBlitzPayFee
    : (fallbackBlitzPayFee > 0.009 ? fallbackBlitzPayFee : 0);
  const blitzPayFeeLabel = blitzPayFeePercent && blitzPayFeePercent > 0
    ? `BlitzPay Fee (${blitzPayFeePercent}%)`
    : 'BlitzPay Fee';

  rows.forEach((row, i) => {
    const rowHeight = row.subDesc ? ROW_H + 3.5 : ROW_H;
    ensureSpace(rowHeight + 2);

    if (i % 2 === 1) fillRect(ML, y, CW, rowHeight, C.stripeBg);

    // Description (wrap to col width)
    const maxW     = T.desc.w - 5;
    const descLine = doc.splitTextToSize(safe(row.desc), maxW)[0];
    sf(false, 8, C.dark);
    tx(descLine, T.desc.x + 2.5, y + 4.8);

    if (row.subDesc) {
      sf(false, 6.5, C.gray);
      tx(row.subDesc, T.desc.x + 2.5, y + 8.5);
    }

    sf(false, 8, C.dark);
    tx(money(row.rate),
      T.rate.x + T.rate.w, y + 4.8, { align: 'right' });
    tx(String(row.qty),
      T.qty.x + T.qty.w / 2, y + 4.8, { align: 'center' });

    sf(true, 8, C.dark);
    tx(money(row.total),
      T.total.x + T.total.w - 1, y + 4.8, { align: 'right' });

    // Row separator
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.15);
    doc.line(ML, y + rowHeight, PW - MR, y + rowHeight);

    y += rowHeight;
  });

  y += 5;

  /* 8 ─ TOTALS (mirrors the component Stack layout) ─────────────── */
  ensureSpace(38);
  const totX = PW - MR - 68;

  const totRow = (label, value, bold, col) => {
    sf(bold, 8, col || C.dark);
    tx(label, totX, y);
    tx(value, PW - MR, y, { align: 'right' });
    y += 5;
  };

  const railFeeDisplay = safeNum(cd?.rail_fee_in_invoice ?? cd?.rail_fee_amount);
  const railLabel = cd?.forced_payment_rail
    ? `Rail Fee (${cd.forced_payment_rail}${cd.rail_fee_pct ? ` ${cd.rail_fee_pct}%` : ''})${cd.rail_fee_payer === 'creator' ? ' — Creator covers' : ''}`
    : 'Rail Fee';
  totRow('Subtotal',    money(displayedSubtotal));
  if (railFeeDisplay > 0) totRow(railLabel, money(railFeeDisplay));
  if (blitzPayFee > 0) totRow(blitzPayFeeLabel, money(blitzPayFee));
  totRow('Tax',         '$0.00');

  hline(y - 1.5, C.border, 0.25); y += 1;
  totRow('Total',       money(amountDue), true);
  totRow('Amount Paid', money(amountPaid));

  hline(y - 1.5, C.border, 0.25); y += 1;

  // Red "Amount Due" row — matches component's red Typography
  sf(true, 9, C.red);
  tx('Amount Due (USD)', totX, y);
  tx(money(outstanding), PW - MR, y, { align: 'right' });
  y += 9;

  /* 9 ─ TERMS ────────────────────────────────────────────────────── */
  ensureSpace(18);
  hline(y, C.border, 0.3);
  y += 5;

  const termsText = TERMS.terms
    || `Make all checks payable to "${BF.display}"`;

  let notesText = TERMS.notes || '';
  if (TERMS.instructions) {
    notesText = notesText ? `${notesText}\n\n${TERMS.instructions}` : TERMS.instructions;
  }
  const shouldShowStandaloneInvoiceNote = Boolean(
    trimmedExternalPO &&
    invoiceNoteValue &&
    !notesText.includes(invoiceNoteValue)
  );

  sf(true, 7.5, C.dark);
  tx('Terms', ML, y); y += 4.5;

  sf(false, 7, C.gray);
  const termsLines = doc.splitTextToSize(termsText, CW);
  doc.text(termsLines, ML, y);
  y += termsLines.length * 3.8 + 2;

  if (notesText) {
    ensureSpace(10);
    const noteLines = doc.splitTextToSize(notesText, CW);
    sf(false, 7, C.gray);
    doc.text(noteLines, ML, y);
    y += noteLines.length * 3.8 + 4;
  }
  if (shouldShowStandaloneInvoiceNote) {
    ensureSpace(10);
    sf(true, 7, C.dark);
    tx('Invoice Note', ML, y); y += 4;
    const invoiceNoteLines = doc.splitTextToSize(invoiceNoteValue, CW);
    sf(false, 7, C.gray);
    doc.text(invoiceNoteLines, ML, y);
    y += invoiceNoteLines.length * 3.8 + 4;
  }

  /* 10 ─ BANKING BOX ─────────────────────────────────────────────── */
  const hasBankInfo = BANK.bank || BANK.acct || BANK.routing;
  if (hasBankInfo) {
    const bankLines = [];
    if (BANK.holder)   bankLines.push(['Account Holder',        BANK.holder]);
    if (BANK.name)     bankLines.push(['Account Name',          BANK.name]);
    bankLines.push(['Address', `${BF.addr1}, ${BF.city}, ${BF.state} ${BF.zip}`]);
    if (BANK.bank)     bankLines.push(['Bank',                  BANK.bank]);
    if (BANK.bankAddr) bankLines.push(['Bank Address',          BANK.bankAddr]);
    if (BANK.acct)     bankLines.push(['Account #',             BANK.acct]);
    if (BANK.routing)  bankLines.push(['ABA / Routing / IBAN',  BANK.routing]);
    if (BANK.swift)    bankLines.push(['SWIFT',                 BANK.swift]);

    const bxH = 9 + bankLines.length * 4.5;
    ensureSpace(bxH + 4);

    fillRect(ML, y, CW, bxH, C.lightBg, C.border, 0.2);
    sf(true, 7.5, C.dark);
    tx('Wire / ACH Payment Details', ML + 3, y + 6);
    y += 9.5;

    bankLines.forEach(([label, val]) => {
      sf(false, 7, C.gray);
      tx(`${label}: `, ML + 3, y);
      const lw = doc.getTextWidth(`${label}: `);
      sf(true, 7, C.dark);
      tx(val, ML + 3 + lw, y);
      y += 4.5;
    });

    y += 4;
  }

  /* 11 ─ Footer on every page ────────────────────────────────────── */
  const totalPages = doc.internal.getNumberOfPages();
  for (let pg = 1; pg <= totalPages; pg++) {
    doc.setPage(pg);
    sf(false, 6.5, C.gray);
    tx(
      `${BF.display}  ·  ${BF.email || DEFAULTS.email}  ·  useblitz.co`,
      PW / 2, PH - 7,
      { align: 'center' },
    );
    if (totalPages > 1) {
      tx(`Page ${pg} of ${totalPages}`, PW - MR, PH - 7, { align: 'right' });
    }
  }

  /* 12 ─ Save ─────────────────────────────────────────────────────── */
  const filename = `BlitzPay_Invoice_${poNumber || invoice?.id || 'invoice'}.pdf`;
  doc.save(filename);
}
